import React, { useState, useCallback } from 'react';
import { Text, Stack, Button, Constrain, ControlLink, AspectRatioBox } from '@moda/om';
import NextIcon from '@moda/icons/next-20';
import { SwiperSlide } from 'swiper/react';
import { useParams } from 'react-router-dom';
import { breakpoints } from '@moda/tokens';
import { remToUnitlessPx } from '../../lib/remToUnitlessPx';
import { hrefFor } from '../../routers';
import { CarouselPumoModuleGridFragment_cells_Look as LookCell } from '../../generated/types/CarouselPumoModuleGridFragment';
import { CarouselCell } from '../Grid';
import { Swiper } from '../Swiper';
import { getLookCellsGridScenario, LookCellsGridScenario } from './getLookCellsGridScenario';

import './LookCellsGrid.scss';

export interface LookCellsGridProps {
  looks: LookCell[];
  galleryViewUrl?: string;
  onClick?: (event: Event) => void;
}

const getSlidesPerColumn = (scenario: string) => {
  if (scenario === 'divisiblebytwo') {
    return 2;
  }
  if (scenario === 'divisiblebythree') {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return 3;
  }
  return 1;
};

export const LookCellsGrid: React.FC<LookCellsGridProps> = ({ looks, onClick }) => {
  const { scenario, hasGalleryViewLinkPlaceholder } = getLookCellsGridScenario(looks.length);

  const { vertical = 'women' } = useParams<{ vertical?: string }>();

  const firstLook = looks[0];
  const href =
    firstLook && firstLook.trunkshowSlugs?.length
      ? hrefFor.LookviewPage({
          vertical: vertical,
          trunkshowSlug: `${firstLook.trunkshowSlugs[0]}`,
          lookId: firstLook.id
        })
      : undefined;

  const [scrollable, setScrollable] = useState(false);

  const setIsScrollable = useCallback(swiper => {
    if (window.innerWidth < swiper.wrapperEl.scrollWidth) {
      setScrollable(true);
    }
  }, []);

  return (
    <Stack space={3}>
      <Swiper
        slidesPerView={4}
        spaceBetween={8}
        slidesPerColumn={getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())}
        slidesPerColumnFill="row"
        grabCursor
        scrollbar={{
          el: '.LookCellsGrid__scrollbar',
          draggable: true,
          dragClass: 'LookCellsGrid__scrollbar-drag'
        }}
        breakpoints={{
          [remToUnitlessPx(breakpoints.xs)]: {
            slidesPerView: 5,
            slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
          },
          [remToUnitlessPx(breakpoints.sm)]: {
            slidesPerView: 8,
            slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
          },
          [remToUnitlessPx(breakpoints.md)]: {
            slidesPerView: 9,
            slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
          },
          [remToUnitlessPx(breakpoints.lg)]: {
            slidesPerView: 10,
            slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
          }
        }}
        onSwiper={swiper => setIsScrollable(swiper)}
        onClick={(swiper, event) => onClick?.(event)}
        className="LookCellsGrid__content-wrapper LookCellsGrid__carousel"
      >
        <>
          {looks.map(carouselCell => (
            <SwiperSlide key={carouselCell.id}>
              <CarouselCell carouselCell={carouselCell} />
            </SwiperSlide>
          ))}

          {hasGalleryViewLinkPlaceholder && (
            <SwiperSlide className="LookCellsGrid__gallery-view-clickable-wrapper" key="lastSlide">
              <ControlLink
                className="LookCellsGrid__gallery-view-clickable"
                underlined={false}
                to={href}
              >
                <AspectRatioBox maxHeight={540} maxWidth={340}>
                  <Stack
                    space={2}
                    alignItems="center"
                    justifyContent="center"
                    className="LookCellsGrid__gallery-view-text"
                  >
                    Gallery
                    <Stack alignItems="center" direction="horizontal" space={2}>
                      View
                      <NextIcon />
                    </Stack>
                  </Stack>
                </AspectRatioBox>
              </ControlLink>
            </SwiperSlide>
          )}
        </>
        <div className="LookCellsGrid__scrollbar" />
      </Swiper>

      <Constrain>
        <Stack space={3}>
          {scrollable && <Text treatment="body1"> {looks.length} looks</Text>}

          <div className="LookCellsGrid__shop-by-look">
            <Button secondary to={href}>
              Shop by Look
            </Button>
          </div>
        </Stack>
      </Constrain>
    </Stack>
  );
};
